import { useCallback, useEffect, useState } from "react";

/**
 * Storage keys for the app install interstitial.
 *
 * SESSION_DISMISSED_KEY — written to sessionStorage when the user closes the
 * interstitial. Cleared automatically when the tab/session ends so the
 * interstitial reappears on the next session.
 *
 * CONVERTED_KEY — written to localStorage when the user clicks any CTA
 * (App Store / Google Play button). Persists indefinitely so a converted
 * user is never shown the interstitial again on this device/browser.
 */
const SESSION_DISMISSED_KEY = "app_install_interstitial_dismissed_session";
const CONVERTED_KEY = "app_install_interstitial_converted";

const readSessionFlag = (key: string): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const writeSessionFlag = (key: string): void => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, "1");
  } catch {
    // sessionStorage unavailable (private mode) — fall through silently
  }
};

const readLocalStorageFlag = (key: string): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const writeLocalStorageFlag = (key: string): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    // localStorage unavailable — fall through silently
  }
};

/**
 * Manages show/hide state for the app install interstitial.
 *
 * shouldShow — true when the interstitial is eligible to be displayed:
 *   - user has not dismissed it in this session
 *   - user has not converted (clicked a CTA) on this device/browser
 *
 * markDismissed — call when the user closes the interstitial (×). Hides it
 *   for the rest of the current session; reappears next session.
 *
 * markConverted — call when the user clicks any CTA (App Store / Google Play).
 *   Permanently suppresses the interstitial on this device/browser.
 *
 * CLEANUP (experiments 102703 / 102704): if this feature is retired without
 * productionisation, consider clearing `app_install_interstitial_converted`
 * from localStorage — otherwise users who converted during the experiment will
 * permanently not see any future app-install prompts on this device/browser.
 */
export const useAppInstallInterstitialStorage = () => {
  const [isDismissedThisSession, setIsDismissedThisSession] = useState(() => readSessionFlag(SESSION_DISMISSED_KEY));
  const [isConverted, setIsConverted] = useState(() => readLocalStorageFlag(CONVERTED_KEY));

  // Listen for localStorage changes from other tabs so that a conversion on
  // the Home page also hides the interstitial on the Performer page tab.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === CONVERTED_KEY && event.newValue === "1") {
        setIsConverted(true);
      }
    };
    window.addEventListener("storage", handleStorageEvent);
    return () => window.removeEventListener("storage", handleStorageEvent);
  }, []);

  const shouldShow = !isDismissedThisSession && !isConverted;

  const markDismissed = useCallback((): void => {
    writeSessionFlag(SESSION_DISMISSED_KEY);
    setIsDismissedThisSession(true);
  }, []);

  const markConverted = useCallback((): void => {
    writeLocalStorageFlag(CONVERTED_KEY);
    setIsConverted(true);
  }, []);

  return { shouldShow, markDismissed, markConverted };
};
